import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { API_BASE } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  const applyUser = (profile) => {
    setUser(profile);
    setIsAuthenticated(true);
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setIsLoading(false);
      return;
    }
    setIsAuthenticated(true);
    fetch(`${API_BASE}/admin/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.data) applyUser(data.data);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  const setSession = (token, profile) => {
    localStorage.setItem('adminToken', token);
    applyUser(profile);
  };

  const clearMustChangePassword = () => {
    setUser((prev) => (prev ? { ...prev, must_change_password: false } : prev));
  };

  const login = async (e) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData),
      });
      const result = await res.json();
      if (result.success) {
        setSession(result.data.token, result.data.user);
        toast.success('Welcome back');
        return { success: true, must_change_password: result.data.user.must_change_password };
      }
      toast.error(result.message || 'Login failed');
      return { success: false };
    } catch {
      toast.error('Login failed');
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setUser(null);
    toast.success('Logged out');
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        mustChangePassword: Boolean(user?.must_change_password),
        loginData,
        setLoginData,
        login,
        logout,
        setSession,
        clearMustChangePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
